import { type RefObject, useCallback, useMemo, useRef, useState } from 'react';
import { SidePanel } from '@carbon/ibm-products';
import { useAtom, useSetAtom } from 'jotai';
import type { DocumentResponse } from 'types/api-responses';
import { fetchStructuredPartiesListAtom } from './atoms/structured-parties';
import {
  clearAddressFlagAtom,
  clearAddressFlagUrl,
} from './atoms/structured-addresses';
import {
  StagedEntityEditor,
  type StagedEntityEditorHandle,
} from './staged-entity-editor';
import { MoveAddressModal } from './move-address-modal';
import { ClearFlagsModal } from './clear-flags-modal';
import { useExclusiveDrawer } from './exclusive-drawer-hooks';
import type { StructuredPartyDetail, StructuredPartyListItem } from './types';
import { SharedAnchorNotice } from './shared-anchor-notice';
import { SharedRenameModal } from './shared-rename-confirm';
import { useMoveAddress, useStagedEditsSave } from './hooks';
import { anchorCard, cardAddresses } from './utils';
import style from './staged-entity-editor.module.scss';

interface StagedEntityEditorPanelProps {
  open: boolean;
  document: DocumentResponse;
  party: StructuredPartyDetail;
  /**
   * All parties on the document — used to populate the Move Address modal's
   * destination picker. Caller usually passes the cached list.
   */
  allParties?: StructuredPartyListItem[];
  onClose: () => void;
  onSaved: () => void;
}

/**
 * The modal row for the address whose review flag is being cleared, labelled
 * from the anchor the editor edits.
 *
 * @param {Pick<StructuredPartyDetail, 'currentAnchor' | 'stagedEntity'>} anchorFields - The party's anchor and staged entity.
 * @param {number} addressId - The address the user clicked.
 * @return {{ id: number, label: string }[]} The single address row.
 */
function clearFlagAddressRows(
  anchorFields: Pick<StructuredPartyDetail, 'currentAnchor' | 'stagedEntity'>,
  addressId: number
) {
  const addr = cardAddresses(anchorCard(anchorFields)).find(
    (a) => a.id === addressId
  );
  return [
    {
      id: addressId,
      label: addr?.fullText || addr?.asFoundAddress || `Address #${addressId}`,
    },
  ];
}

interface EditorPanelContentProps {
  editorRef: RefObject<StagedEntityEditorHandle>;
  party: StructuredPartyDetail;
  error: string | null;
  pending: boolean;
  // Remounts the editor after each successful save.
  savedCount: number;
  onMoveAddress: (addressId: number) => void;
  onClearFlag: (addressId: number) => void;
}

/**
 * The panel's body: the shared-anchor notice while unsaved edits touch the
 * anchor's own fields, the last error, and the editor form.
 *
 * @param {EditorPanelContentProps} props - The component props.
 * @return {JSX.Element} The panel content.
 */
function EditorPanelContent({
  editorRef,
  party,
  error,
  pending,
  savedCount,
  onMoveAddress,
  onClearFlag,
}: EditorPanelContentProps) {
  const [touchesShared, setTouchesShared] = useState(false);
  return (
    <div className={style.panelContent}>
      {touchesShared && (
        <SharedAnchorNotice
          sharedWith={party.sharedWith}
          subject="Changing the name, addresses or phone numbers"
        />
      )}
      {error && (
        <div role="alert" className={style.errorMessage}>
          {error}
        </div>
      )}
      <StagedEntityEditor
        key={savedCount}
        ref={editorRef}
        party={party}
        disabled={pending}
        onSharedEditChange={setTouchesShared}
        onMoveAddress={onMoveAddress}
        onClearFlag={onClearFlag}
      />
    </div>
  );
}

/**
 * Side panel editing the party's anchor details (name, role, addresses, phone
 * numbers), with the Move Address and Clear Flag modals its rows open.
 *
 * @param {StagedEntityEditorPanelProps} props - The component props.
 * @return {JSX.Element} The panel and its modals.
 */
export function StagedEntityEditorPanel({
  open,
  document,
  party,
  allParties = [],
  onClose,
  onSaved,
}: StagedEntityEditorPanelProps) {
  const ref = useRef<StagedEntityEditorHandle>(null);
  const [clearFlagState, triggerClearFlag] = useAtom(clearAddressFlagAtom);
  const [error, setError] = useState<string | null>(null);
  const [clearFlagAddressId, setClearFlagAddressId] = useState<number | null>(
    null
  );
  // Bumped by each successful save: the saved edits are committed, so the form
  // starts over and takes the saved rows (new ones with their ids) from the
  // refreshed party.
  const [savedCount, setSavedCount] = useState(0);
  const edits = useStagedEditsSave({
    documentId: document.id,
    party,
    failure: 'Could not save changes',
    onStart: () => setError(null),
    onError: setError,
  });
  const reloadList = useSetAtom(fetchStructuredPartiesListAtom);
  const pending = edits.saving || clearFlagState.loading;
  const pageContentSelector = useExclusiveDrawer(
    'staged-entity-editor',
    open,
    onClose
  );

  const moveAddress = useMoveAddress({
    document,
    allParties,
    party,
    onMoved: onSaved,
  });

  const handleClearFlag = useCallback((addressId: number) => {
    setError(null);
    setClearFlagAddressId(addressId);
  }, []);

  const clearFlagsModalAddresses = useMemo(
    () =>
      clearFlagAddressId === null
        ? []
        : clearFlagAddressRows(
            {
              currentAnchor: party.currentAnchor,
              stagedEntity: party.stagedEntity,
            },
            clearFlagAddressId
          ),
    [clearFlagAddressId, party.currentAnchor, party.stagedEntity]
  );

  const confirmClearFlag = useCallback(
    (ids: number[]) => {
      if (ids.length === 0) return;
      triggerClearFlag({
        url: clearAddressFlagUrl(document.id, ids[0]),
        type: 'POST',
        data: null,
        onSuccess: () => {
          setClearFlagAddressId(null);
          const partiesListHref = document.resources.structuredParties?.href;
          if (partiesListHref) reloadList(partiesListHref);
          onSaved();
        },
        onError: (err?: { error?: string }) => {
          setClearFlagAddressId(null);
          setError(err?.error ?? 'Could not clear flag.');
        },
      });
    },
    [
      document.id,
      document.resources.structuredParties?.href,
      triggerClearFlag,
      reloadList,
      onSaved,
    ]
  );

  /** Saves the form's changes; with none, closes without a request. */
  const handleSave = () =>
    edits.save(ref.current, {
      onUnchanged: onClose,
      onSaved: () => {
        setSavedCount((n) => n + 1);
        onSaved();
        onClose();
      },
    });

  return (
    <>
      <SidePanel
        open={open}
        onRequestClose={onClose}
        title="Staged Entity Editor"
        subtitle={`Party · ${party.role || '—'}`}
        placement="right"
        size="md"
        slideIn
        selectorPageContent={pageContentSelector}
        actions={[
          { kind: 'secondary' as const, label: 'Cancel', onClick: onClose },
          {
            kind: 'primary' as const,
            label: pending ? 'Saving…' : 'Save',
            disabled: pending,
            onClick: handleSave,
          },
        ]}
      >
        <EditorPanelContent
          editorRef={ref}
          party={party}
          error={error}
          pending={pending}
          savedCount={savedCount}
          onMoveAddress={() => moveAddress.open()}
          onClearFlag={handleClearFlag}
        />
      </SidePanel>

      <MoveAddressModal {...moveAddress.modalProps} />

      <ClearFlagsModal
        open={clearFlagAddressId !== null}
        addresses={clearFlagsModalAddresses}
        loading={clearFlagState.loading}
        error={error}
        onClose={() => setClearFlagAddressId(null)}
        onConfirm={confirmClearFlag}
      />

      <SharedRenameModal {...edits.renameModalProps} />
    </>
  );
}
